import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const desktopSlides = [
  {
    src: '/assets/Videos/hall.mp4',
    heading: ['Designing', 'Your Dreams'],
  },
  {
    src: '/assets/Videos/bed.mp4',
    heading: ['Crafting', 'Elegant Spaces'],
  },
  {
    src: '/assets/Videos/kitchen.mp4',
    heading: ['Modern', 'Kitchen Interiors'],
  },
];

const mobileSlides = [
  {
    src: '/assets/Videos/mobile1.mp4',
    heading: ['Designing', 'Your Dreams'],
  },
  {
    src: '/assets/Videos/mobile2.mp4',
    heading: ['Crafting', 'Elegant Spaces'],
  },
  {
    src: '/assets/Videos/mobile3.mp4',
    heading: ['Modern', 'Kitchen Interiors'],
  },
];

const getSlides = (isMobile) => (isMobile ? mobileSlides : desktopSlides);

const Hero = () => {
  const swiperRef = useRef(null);
  const videoRefs = useRef([]);
  const progressFillRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const slides = getSlides(isMobile);

  const [activeIndex, setActiveIndex] = useState(0);
  const [textKey, setTextKey] = useState(0);

  // Track isMobile changes to reset slides & player references
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(prev => {
        if (prev !== mobile) {
          videoRefs.current.forEach(vid => {
            if (vid) {
              try {
                vid.pause();
                vid.currentTime = 0;
              } catch (e) {}
            }
          });
          videoRefs.current = [];
          setActiveIndex(0);
          if (progressFillRef.current) progressFillRef.current.style.width = '0%';
          setTextKey(k => k + 1);
          return mobile;
        }
        return prev;
      });
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const safePlay = useCallback((vid) => {
    if (!vid) return;
    vid.defaultMuted = true;
    vid.muted = true;
    vid.playsInline = true;
    if (vid.readyState === 0) {
      try {
        vid.load();
      } catch (e) {}
    }
    const playPromise = vid.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Fallback for mobile browsers low power mode or strict autoplay restrictions:
        const retryPlay = () => {
          if (vid) {
            vid.defaultMuted = true;
            vid.muted = true;
            vid.play().catch(() => {});
          }
          window.removeEventListener('touchstart', retryPlay);
          window.removeEventListener('click', retryPlay);
          window.removeEventListener('scroll', retryPlay);
          window.removeEventListener('pointerdown', retryPlay);
        };
        window.addEventListener('touchstart', retryPlay, { once: true, passive: true });
        window.addEventListener('click', retryPlay, { once: true, passive: true });
        window.addEventListener('scroll', retryPlay, { once: true, passive: true });
        window.addEventListener('pointerdown', retryPlay, { once: true, passive: true });
      });
    }
  }, []);

  const activateSlide = useCallback((index) => {
    const videos = videoRefs.current;
    videos.forEach((vid, i) => {
      if (!vid) return;
      if (i === index) {
        try {
          if (vid.readyState === 0) {
            vid.load();
          }
          vid.currentTime = 0;
        } catch (e) {}
        safePlay(vid);
      } else {
        try {
          vid.pause();
          vid.currentTime = 0;
        } catch (e) {}
      }
    });

    setActiveIndex(index);
    if (progressFillRef.current) progressFillRef.current.style.width = '0%';
    setTextKey(k => k + 1);
  }, [safePlay]);

  const handleVideoEnded = (currentIndex) => {
    if (slides.length <= 1) {
      const vid = videoRefs.current[0];
      if (vid) {
        try {
          vid.currentTime = 0;
        } catch (e) {}
        safePlay(vid);
      }
      return;
    }
    const next = (currentIndex + 1) % slides.length;
    if (swiperRef.current) {
      swiperRef.current.slideTo(next);
    } else {
      activateSlide(next);
    }
  };

  const handleTimeUpdate = (index, e) => {
    if (index === activeIndex) {
      const vid = e.target;
      if (vid && vid.duration && isFinite(vid.duration) && progressFillRef.current) {
        const pct = (vid.currentTime / vid.duration) * 100;
        progressFillRef.current.style.width = `${pct}%`;
      }
    }
  };

  const handleSlideChange = (swiper) => {
    activateSlide(swiper.activeIndex);
  };

  // Play initial active video on mount or mode switch
  useEffect(() => {
    const vid = videoRefs.current[0];
    if (!vid) return;

    const playFirst = () => {
      try {
        vid.currentTime = 0;
      } catch (e) {}
      safePlay(vid);
    };

    if (vid.readyState >= 2) {
      playFirst();
    } else {
      if (vid.readyState === 0) {
        try {
          vid.load();
        } catch (e) {}
      }
      vid.addEventListener('canplay', playFirst, { once: true });
      vid.addEventListener('loadeddata', playFirst, { once: true });
    }
  }, [isMobile, safePlay]);

  return (
    <div className="hero hero-slider-layout">
      {/* ── Video stack: stacked absolutely, CSS crossfade ── */}
      <div className="hero-video-stack">
        {slides.map((slide, i) => (
          <video
            key={`${isMobile ? 'm' : 'd'}-${i}`}
            ref={(el) => {
              if (el) {
                el.defaultMuted = true;
                el.muted = true;
                videoRefs.current[i] = el;
              }
            }}
            src={slide.src}
            autoPlay={i === activeIndex}
            muted
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            disablePictureInPicture
            controlsList="nodownload"
            preload="auto"
            onCanPlay={(e) => {
              if (i === activeIndex) {
                safePlay(e.target);
              }
            }}
            onTimeUpdate={(e) => handleTimeUpdate(i, e)}
            onEnded={() => handleVideoEnded(i)}
            className={`hero-local-video ${i === activeIndex ? 'hero-video-active' : ''}`}
          />
        ))}
        <div className="hero-vignette" />
      </div>

      {/* ── Swiper: transparent gesture/navigation layer ── */}
      <Swiper
        key={`${isMobile ? 'm' : 'd'}-${slides.length}`}
        className="hero-swiper-nav"
        slidesPerView={1}
        speed={800}
        loop={false}
        allowTouchMove={true}
        modules={[Pagination]}
        onSwiper={(swiper) => { swiperRef.current = swiper; }}
        onSlideChange={handleSlideChange}
      >
        {slides.map((_, i) => (
          <SwiperSlide key={i} />
        ))}
      </Swiper>

      {/* ── Minimal text — re-animates on each slide ── */}
      <div className="hero-cinema-content" key={textKey}>
       <h1 className="hero-cinema-heading">
  {activeIndex === 0 ? (
    <>
      <span
        className="hero-heading-line"
        style={{ animationDelay: '0.05s' }}
      >
        Designing Your Dreams
      </span>
      <span
        className="hero-heading-line hero-seo-text"
        style={{ animationDelay: '0.15s' }}
      >
        Interior Designer in Nagpur
      </span>
    </>
  ) : (
    slides[activeIndex]?.heading.map((line, i) => (
      <span
        key={i}
        className="hero-heading-line"
        style={{ animationDelay: `${0.05 + i * 0.1}s` }}
      >
        {line}
      </span>
    ))
  )}
</h1>
        <div className="hero-accent-line" />
      </div>

      {/* ── Bottom controls bar ── */}
      <div className="hero-controls-bar">
        {slides.length > 1 && (
          <div className="hero-bullets">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`hero-bullet ${i === activeIndex ? 'active' : ''}`}
                onClick={() => swiperRef.current?.slideTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
        <div className="hero-progress-track">
          <div className="hero-progress-fill" ref={progressFillRef} style={{ width: '0%' }} />
        </div>
        {slides.length > 1 && (
          <div className="hero-counter">
            <span className="hero-counter-current">0{activeIndex + 1}</span>
            <span className="hero-counter-sep"> / </span>
            <span className="hero-counter-total">0{slides.length}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hero;
